import React from 'react';
import { Drawer, IconButton, Stack, Typography, Box, Button, TextField, Grid2 as Grid, Paper, Divider } from '@mui/material';
import { Cancel01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useAppDispatch, useAppSelector, useResponsive } from '@/hooks';
import { closeNewClientFormDrawer } from '@/store/uiSlice';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useAddCustomerMutation } from "@/features/organizations/api/orgCustomersApi";
import useSnackbar from "@/hooks/useSnackbar";
import { AnonymousUser } from "@/types";
import { buildDeliveryLocation } from "@/utils/location";
import InputMask from "react-input-mask";
import { sanitizePhone } from "@/utils/phones";
// import NeighborhoodAutocomplete from "@/components/NeighborhoodAutocomplete";
import { sanitizeForSearch } from "@/utils/names";
import LoadingButton from '@mui/lab/LoadingButton';

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Campo requerido'),
  lastname: Yup.string().required('Campo requerido'),
  phone: Yup.string().required('Campo requerido'),
  locations: Yup.array().of(
    Yup.object().shape({
      address: Yup.string().required('Campo requerido'),
      // neighborhood: Yup.string().required('Campo requerido'),
    })
  )
});

const initialValues: AnonymousUser = {
  name: '',
  lastname: '',
  phone: '',
  sanitizedName: '',
  sanitizedLastname: '',
  locations: [
    {
      id: 'default',
      address: '',
      // neighborhood: '',
      house: '',
    }
  ]
};

const NewClientFormDrawer: React.FC = () => {
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector((state) => state.ui.isNewClientFormDrawerOpen);
  const organizationId = useAppSelector((store) => store.ui.activeOrganization);
  const [openSnackbar] = useSnackbar();
  const { isMobile } = useResponsive();
  const direction = isMobile ? "column" : "row";
  const [addUser, { isLoading }] = useAddCustomerMutation();

  const handleClose = () => {
    dispatch(closeNewClientFormDrawer());
  };

  const handleSubmit = async (values: AnonymousUser, { resetForm }: any) => {
    const payload = {
      ...values,
      sanitizedName: sanitizeForSearch(values.name),
      sanitizedLastname: sanitizeForSearch(values.lastname),
      deliveryLocation: buildDeliveryLocation(values.locations ?? []),
    };

    const response = await addUser({
      organizationId,
      draft: payload as AnonymousUser,
    });

    if (!response?.error) {
      openSnackbar("Cliente creado exitosamente", "success");
      resetForm();
      handleClose();
    } else {
      openSnackbar(response.error.message, "error");
    }
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={handleClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 700 }, p: 0, bgcolor: "#DDEAFD" }
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        p={2}
      >
        <Typography variant="h2">Registrar Cliente</Typography>

        <IconButton color="primary" size="large" component={Paper} elevation={2} onClick={handleClose} sx={{ backgroundColor: "#ffffff" }}>
          <HugeiconsIcon icon={Cancel01Icon} />
        </IconButton>
      </Stack>
      <Divider />
      <Box sx={{ p: 0, height: 'calc(100vh - 70px)', overflowY: 'auto' }}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, handleBlur, errors, touched, isSubmitting }) => (
            <Form>
              <Stack spacing={3} p={3}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <Stack direction={direction} spacing={2}>
                      <Paper sx={{ p: 2, flex: 1 }}>
                        <Stack spacing={2}>
                          <Typography variant="h4">Cliente</Typography>
                          <Stack spacing={2}>
                            <TextField
                              label="Nombre"
                              value={values.name}
                              onChange={(e) => setFieldValue("name", e.target.value)}
                              onBlur={handleBlur}
                              error={touched.name && Boolean(errors.name)}
                              helperText={touched.name && errors.name}
                              fullWidth
                              size="small"
                            />
                            <TextField
                              label="Apellido"
                              value={values.lastname}
                              onChange={(e) => setFieldValue("lastname", e.target.value)}
                              onBlur={handleBlur}
                              error={touched.lastname && Boolean(errors.lastname)}
                              helperText={touched.lastname && errors.lastname}
                              fullWidth
                              size="small"
                            />
                          </Stack>
                        </Stack>
                      </Paper>

                      <Paper sx={{ p: 2, flex: 1 }}>
                        <Stack spacing={2}>
                          <Typography variant="h4">Contacto</Typography>
                          <Stack spacing={2}>
                            <InputMask
                              mask="9999-9999"
                              placeholder="0000-0000"
                              alwaysShowMask={false}
                              value={values.phone}
                              onChange={(e: any) => setFieldValue("phone", sanitizePhone(e.target.value))}
                              onBlur={handleBlur}
                            >
                              {() => (
                                <TextField
                                  label="Whatsapp"
                                  error={touched.phone && Boolean(errors.phone)}
                                  helperText={touched.phone && errors.phone}
                                  fullWidth
                                  size="small"
                                />
                              )}
                            </InputMask>
                          </Stack>
                        </Stack>
                      </Paper>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <Paper sx={{ p: 2 }}>
                      <Stack spacing={2}>
                        <Typography variant="h4">Dirección</Typography>
                        <Stack spacing={2}>
                          <Stack spacing={2} direction={direction}>
                            {/* <NeighborhoodAutocomplete
                              fullWidth
                              size="small"
                              value={values.locations?.[0]?.neighborhood ?? ""}
                              onChange={(value) => {
                                setFieldValue("locations[0].neighborhood", value);
                              }}
                              error={touched.locations?.[0]?.neighborhood && Boolean((errors.locations as any)?.[0]?.neighborhood)}
                              helperText={touched.locations?.[0]?.neighborhood && (errors.locations as any)?.[0]?.neighborhood}
                            /> */}
                            <TextField
                              label="No. Casa / Referencia"
                              value={values.locations?.[0]?.house ?? ""}
                              onChange={(e) => setFieldValue("locations[0].house", e.target.value)}
                              fullWidth
                              size="small"
                            />
                          </Stack>
                          <TextField
                            label="Dirección"
                            multiline
                            rows={4}
                            value={values.locations?.[0]?.address ?? ""}
                            onChange={(e) => setFieldValue("locations[0].address", e.target.value)}
                            onBlur={handleBlur}
                            error={touched.locations?.[0]?.address && Boolean((errors.locations as any)?.[0]?.address)}
                            helperText={touched.locations?.[0]?.address && (errors.locations as any)?.[0]?.address}
                            fullWidth
                            size="small"
                          />
                        </Stack>
                      </Stack>
                    </Paper>
                  </Grid>
                </Grid>

                <Stack direction="row" justifyContent="start" spacing={2}>
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    loading={isLoading}
                    size="large"
                    color="success"
                  >
                    Crear
                  </LoadingButton>
                  <Button
                    size="large"
                    variant="outlined"
                    onClick={handleClose}
                  >
                    Cancelar
                  </Button>
                </Stack>
              </Stack>
            </Form>
          )}
        </Formik>
      </Box>
    </Drawer>
  );
};

export default NewClientFormDrawer;
